import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import MarketingTextSection from "../MarketingText/MarketingText";
import { useToast } from "../../../context/ToastContext";
import {
  useGetHolidayTypes,
  getCountryHolidayTypes,
} from "../../../api/geo/useHolidayTypes";
import GeoImageUpload from "../GeoImageUpload/GeoImageUpload";
import { flagSrcForCode } from "../../../utils/countryFlags";
import styles from "./AddCountryModal.module.css";

const emptyForm = {
  name: "",
  iso: "",
  isEU: false,
  isVatCountry: false,
  active: true,
  isPopularDestination: false,
  popularDestinationOrder: "",
  mainImage: "",
  holidayTypeIds: [],
  marketing: {
    title: "",
    para1: "",
    para2: ""
  }
};

// Map API row to form (supports both API shape and legacy shape)
const toForm = (data) => {
  if (!data) return emptyForm;
  const iso = data.iso ?? data.code ?? data.isoCode ?? "";
  const isEU = data.isEU ?? data.euMember ?? false;
  const isVat = data.isVatCountry ?? data.vatCountry ?? false;
  const holidayTypeIds = Array.isArray(data.holidayTypeIds)
    ? [...data.holidayTypeIds]
    : Array.isArray(data.holidayTypes)
    ? data.holidayTypes.map((t) => t.id)
    : [];
  return {
    name: data.name || "",
    iso: typeof iso === "string" ? iso : "",
    isEU: !!isEU,
    isVatCountry: !!isVat,
    active: data.active ?? true,
    isPopularDestination: !!data.isPopularDestination,
    // A text box, so a stored null becomes "" rather than the string "null".
    popularDestinationOrder:
      data.popularDestinationOrder === null || data.popularDestinationOrder === undefined
        ? ""
        : String(data.popularDestinationOrder),
    mainImage: data.mainImage || "",
    holidayTypeIds,
    marketing: {
      title: data.marketing?.title ?? data.title ?? "",
      para1: data.marketing?.para1 ?? data.paragraph1 ?? "",
      para2: data.marketing?.para2 ?? data.paragraph2 ?? ""
    }
  };
};

const AddCountryModal = ({ open, onClose, mode = "add", initialData, onSave, saveLoading = false }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  const { data: allHolidayTypes = [] } = useGetHolidayTypes({
    page: 1,
    limit: 500,
    languageCode: "ENG",
  });
  const holidayTypeOptions = Array.isArray(allHolidayTypes) ? allHolidayTypes : [];

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setForm(toForm(initialData));
      // Fetch assigned holiday types for this country
      getCountryHolidayTypes(initialData.id, { languageCode: "ENG" })
        .then((res) => {
          if (res?.success && Array.isArray(res.data)) {
            const ids = res.data.map((t) => t.id);
            setForm((prev) => ({ ...prev, holidayTypeIds: ids }));
          }
        })
        .catch(() => {});
    } else if (mode === "add") {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleMarketingChange = (marketing) => {
    setForm((prev) => ({
      ...prev,
      marketing,
    }));
  };

  const toggleHolidayType = (typeId) => {
    setForm((prev) => {
      const current = prev.holidayTypeIds || [];
      const next = current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId];
      return { ...prev, holidayTypeIds: next };
    });
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast("Country name is required", "error");
      return;
    }

    if (form.iso.length !== 2) {
      showToast("ISO code must be exactly 2 characters", "error");
      return;
    }

    const code = form.iso.trim().toUpperCase();
    const payload = {
      code,
      isoCode: code,
      name: form.name.trim(),
      euMember: form.isEU,
      vatCountry: form.isVatCountry,
      active: form.active,
      // Only an active country can be popular, and the server refuses it otherwise — this
      // keeps the form from sending something it has already greyed out.
      isPopularDestination: form.active && form.isPopularDestination,
      // "" → null: no rank, rather than a rank of zero.
      popularDestinationOrder:
        form.active && form.isPopularDestination && String(form.popularDestinationOrder).trim() !== ""
          ? Number(form.popularDestinationOrder)
          : null,
      mainImage: form.mainImage || null,
      description: "",
      title: form.marketing.title.trim() || undefined,
      paragraph1: form.marketing.para1.trim() || undefined,
      paragraph2: form.marketing.para2.trim() || undefined,
      holidayTypeIds: form.holidayTypeIds || [],
    };

    if (onSave) {
      onSave(payload);
    } else {
      onClose();
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Country" : "Add New Country"}
      width="700px"
      footer={
        <>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "+ Create Country"
            )}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>Country Name</label>
          <input
            placeholder="e.g. Belgium"
            value={form.name}
            onChange={e => handleChange("name", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>ISO Code</label>
          <input
            placeholder="e.g. BE"
            maxLength={2}
            value={form.iso}
            disabled={mode === "edit"}
            onChange={e =>
              handleChange("iso", e.target.value.toUpperCase())
            }
          />
          {flagSrcForCode(form.iso) && (
            <img
              src={flagSrcForCode(form.iso)}
              alt={`${form.iso} flag`}
              style={{ width: 40, height: 28, objectFit: "cover", borderRadius: 4, marginTop: 8, boxShadow: "0 0 0 1px rgba(0,0,0,0.1)" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </div>

        <div className={styles.field}>
          <label>Main Image</label>
          <GeoImageUpload value={form.mainImage} onChange={(url) => handleChange("mainImage", url)} />
        </div>

        <div className={styles.toggleRow}>
          <div className={styles.field}>
            <label>EU Member</label>
            <ToggleSwitch
              checked={form.isEU}
              onChange={() => handleChange("isEU", !form.isEU)}
            />
          </div>

          <div className={styles.field}>
            <label>VAT Country</label>
            <ToggleSwitch
              checked={form.isVatCountry}
              onChange={() =>
                handleChange("isVatCountry", !form.isVatCountry)
              }
            />
          </div>

          <div className={styles.field}>
            <label>Status</label>
            <ToggleSwitch
              checked={form.active}
              onChange={() => handleChange("active", !form.active)}
            />
          </div>

          <div className={styles.field}>
            <label title="Shown first in the website's destination picker. Never changes prices or which countries a search covers.">
              Popular Destination
            </label>
            <ToggleSwitch
              checked={form.active && form.isPopularDestination}
              disabled={!form.active}
              onChange={() =>
                handleChange("isPopularDestination", !form.isPopularDestination)
              }
            />
          </div>

          {form.active && form.isPopularDestination && (
            <div className={styles.field}>
              <label title="1 shows first. Leave empty to list it after the numbered ones.">
                Popular order
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="1 = first"
                value={form.popularDestinationOrder}
                onChange={(e) =>
                  handleChange("popularDestinationOrder", e.target.value)
                }
              />
            </div>
          )}
        </div>

        <div className={styles.holidayTypesSection}>
          <div className={styles.holidayTypesHeader}>
            <span className={styles.holidayTypesLabel}>Holiday types applied to this country</span>
            {(form.holidayTypeIds || []).length > 0 && (
              <span className={styles.selectedBadge}>{(form.holidayTypeIds || []).length} selected</span>
            )}
          </div>
          {holidayTypeOptions.length === 0 ? (
            <div className={styles.noTypesHint}>No holiday types available. Add types first.</div>
          ) : (
            <div className={styles.holidayTypesGrid}>
              {holidayTypeOptions.map((type) => (
                <label
                  key={type.id}
                  className={`${styles.holidayTypeItem} ${(form.holidayTypeIds || []).includes(type.id) ? styles.holidayTypeItemSelected : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={(form.holidayTypeIds || []).includes(type.id)}
                    onChange={() => toggleHolidayType(type.id)}
                  />
                  <span className={styles.holidayTypeIcon}>{type.icon || "•"}</span>
                  <span>{type.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <MarketingTextSection
        value={form.marketing}
        onChange={handleMarketingChange}
      />

      <div className={styles.infoBox}>
        ⚠️ Country settings affect VAT calculation, hotel VAT rules, pricing, and reports.
      </div>
    </BaseModal>
  );
};

export default AddCountryModal;
